/* eslint-disable func-names */
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Link } from 'gatsby';
import LoadingButton from '@mui/lab/LoadingButton';
import { defaultColors } from 'styles';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';

const defaultStyle = {
  borderRadius: '0.375rem',
  fontFamily: "'Open Sans', 'Segoe UI', Tahoma, sans-serif",
  fontSize: '0.875rem',
  fontWeight: '700',
  // minWidth: '7.5em',
  padding: '0.714em 1.857em',
  position: 'relative',
  overflow: 'hidden'
};

const SharedButton = forwardRef((props, ref) => {
  const {
    ariaLabel,
    children,
    className,
    color = 'primary',
    disabled = false,
    htmlFor,
    loading,
    loadingButton,
    loadingSpinnerSize = '1.625rem',
    loadingSpinnerColor = '#ffffff',
    onClick,
    shine,
    shineDuration = 1,
    size = 'medium',
    startIcon,
    style = {},
    to,
    type = 'button',
    variant = 'contained'
  } = props;
  const theme = createTheme({
    palette: {
      primary: {
        main: defaultColors.primary,
        light: alpha(defaultColors.primary, 0.5),
        dark: alpha(defaultColors.primary, 0.9),
        contrastText: '#ffffff'
      },
      secondary: {
        main: defaultColors.secondary,
        light: alpha(defaultColors.secondary, 0.5),
        dark: alpha(defaultColors.secondary, 0.9),
        contrastText: '#ffffff'
      },
      action: {
        disabledBackground: disabled ? '#c9c9c9' : '',
        color: '#c4c4c4'
      }
    }
  });
  return (
    <ThemeProvider theme={theme}>
      {loadingButton ? (
        <MaterialLoadingButton
          aria-label={ariaLabel}
          className={className}
          loadingIndicator={
            <CircularProgress style={{ color: loadingSpinnerColor }} size={loadingSpinnerSize} />
          }
          color={color}
          disableElevation
          disabled={disabled}
          loading={loading}
          onClick={onClick}
          htmlFor={htmlFor}
          ref={ref}
          size={size}
          sx={{
            ...defaultStyle,
            ...style
          }}
          type={type}
          variant={variant}>
          {children}
          {shine && !loading && <Shine $shineDuration={shineDuration} />}
        </MaterialLoadingButton>
      ) : (
        <MaterialButton
          aria-label={ariaLabel}
          className={className}
          color={color}
          disableElevation
          disabled={disabled}
          htmlFor={htmlFor}
          onClick={onClick}
          ref={ref}
          size={size}
          to={to}
          component={to ? Link : null}
          startIcon={startIcon}
          sx={{
            ...defaultStyle,
            ...style
          }}
          type={type}
          variant={variant}>
          {children}
          {shine && <Shine $shineDuration={shineDuration} />}
        </MaterialButton>
      )}
    </ThemeProvider>
  );
});

export const Shine = styled.span`
  animation: shine-animation ${({ $shineDuration }) => $shineDuration}s 1.5s infinite;
  background: linear-gradient(
    to right,
    rgb(255 255 255 / 0%) 0%,
    rgb(255 255 255 / 80%) 25%,
    rgb(128 186 232 / 0%) 49%,
    rgb(125 185 232 / 0%) 50%,
    rgb(125 185 232 / 0%) 100%
  );
  content: '';
  height: 100%;
  position: absolute;
  top: 0;
  transform: translateX(100%);
  width: 100%;
  z-index: 1;

  @keyframes shine-animation {
    0% {
      transform: translateX(-100%);
    }

    25% {
      transform: translateX(100%);
    }

    100% {
      transform: translateX(100%);
    }
  }
`;

const MaterialButton = styled(Button)`
  .MuiButton-text {
    text-decoration: underline;
  }

  .MuiButton-sizeSmall {
    font-size: 0.75rem !important;
  }

  a {
    color: #004d9a;
  }
`;

const MaterialLoadingButton = styled(LoadingButton)`
  a {
    color: #004d9a;
  }
`;

export default SharedButton;
